/**
 * Service Import Cache
 * Provides singleton caching for dynamic service imports to improve performance
 */
import { logger } from './loggingService';

interface CachedService {
  module: any;
  timestamp: number;
}

type ServiceKey =
  | 'blueprintGeneratorService'
  | 'gapAnalysisService'
  | 'subscriptionService'
  | 'githubProjectImportService'
  | 'githubScaffoldService'
  | 'projectSetupService'
  | 'realtimeAnalysisService'
  | 'websocketService'
  | 'autoPopulationService';

class ServiceCache {
  private cache = new Map<string, CachedService>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get a service module with caching
   */
  async getService<T = any>(serviceKey: ServiceKey): Promise<T> {
    const cached = this.cache.get(serviceKey);
    const now = Date.now();

    // Return cached service if still valid
    if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
      return cached.module;
    }

    try {
      // Import the service via explicit map to ensure bundler resolution
      const module = await this.importByKey(serviceKey);
      
      // Cache the service
      this.cache.set(serviceKey, {
        module,
        timestamp: now
      });

      return module;
    } catch (error) {
      logger.error('Failed to import service', { error, serviceKey });
      throw error;
    }
  }

  /**
   * Get a specific service class from a module
   */
  async getServiceClass<T = any>(
    serviceKey: ServiceKey, 
    className: string
  ): Promise<T> {
    const module = await this.getService(serviceKey);
    
    if (!module[className]) {
      throw new Error(`Service class '${className}' not found for ${serviceKey}`);
    }

    return module[className];
  }

  /**
   * Clear cache for a specific service
   */
  clearService(serviceKey: ServiceKey): void {
    this.cache.delete(serviceKey);
  }

  /**
   * Clear all cached services
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      services: Array.from(this.cache.keys()),
      oldestEntry: Math.min(...Array.from(this.cache.values()).map(s => s.timestamp)),
      newestEntry: Math.max(...Array.from(this.cache.values()).map(s => s.timestamp))
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [path, service] of this.cache.entries()) {
      if ((now - service.timestamp) >= this.CACHE_TTL) {
        this.cache.delete(path);
      }
    }
  }

  // Internal: map service keys to importers
  private async importByKey(key: ServiceKey): Promise<ServiceModule> {
    switch (key) {
      case 'blueprintGeneratorService':
        return importBlueprintGenerator();
      case 'gapAnalysisService':
        return importGapAnalysis();
      case 'subscriptionService':
        return importSubscription();
      case 'githubProjectImportService':
        return importGitHubProjectImport();
      case 'githubScaffoldService':
        return importGitHubScaffold();
      case 'projectSetupService':
        return importProjectSetup();
      case 'realtimeAnalysisService':
        return importRealtimeAnalysis();
      case 'websocketService':
        return importWebSocket();
      case 'autoPopulationService':
        return importAutoPopulation();
      default:
        throw new Error(`Unknown service key: ${key}`);
    }
  }
}

// Export singleton instance with lazy initialization to avoid TDZ errors
let _serviceCache: ServiceCache | null = null;
const getServiceCache = (): ServiceCache => {
  if (!_serviceCache) {
    _serviceCache = new ServiceCache();
  }
  return _serviceCache;
};

export const serviceCache = new Proxy({} as ServiceCache, {
  get(_target, prop) {
    const instance = getServiceCache();
    const value = (instance as any)[prop];
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    return value;
  },
  set(_target, prop, value) {
    const instance = getServiceCache();
    (instance as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    const instance = getServiceCache();
    return prop in instance;
  },
});

// Convenience functions for common services
export const getService = <T = any>(serviceKey: ServiceKey): Promise<T> => 
  serviceCache.getService<T>(serviceKey);

export const getServiceClass = <T = any>(
  serviceKey: ServiceKey, 
  className: string
): Promise<T> => 
  serviceCache.getServiceClass<T>(serviceKey, className);

// Explicit import map ensures Vite/Rollup can statically analyze dynamic imports
// and generate the corresponding chunks.
// Note: keys must match the ServiceKey union above.
 
type ServiceModule = any;
async function importBlueprintGenerator(): Promise<ServiceModule> {
  return import('./blueprintGeneratorService');
}
async function importGapAnalysis(): Promise<ServiceModule> {
  return import('./gapAnalysisService');
}
async function importSubscription(): Promise<ServiceModule> {
  return import('./subscriptionService');
}
async function importGitHubProjectImport(): Promise<ServiceModule> {
  return import('./githubProjectImportService');
}
async function importGitHubScaffold(): Promise<ServiceModule> {
  return import('./githubScaffoldService');
}
async function importProjectSetup(): Promise<ServiceModule> {
  return import('./projectSetupService');
}
async function importRealtimeAnalysis(): Promise<ServiceModule> {
  return import('./realtimeAnalysisService');
}
async function importWebSocket(): Promise<ServiceModule> {
  return import('./websocketService');
}
async function importAutoPopulation(): Promise<ServiceModule> {
  return import('./autoPopulationService');
}

 

// Auto-cleanup every 10 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    serviceCache.cleanup();
  }, 10 * 60 * 1000);
}
